'use client'

import { type ISbStoryData } from '@storyblok/react/rsc'
import {
  createContext,
  type FC,
  type PropsWithChildren,
  useContext,
  useMemo,
} from 'react'

import { type CachedEnrichedStoryResponseGetCachedEnrichedStoryEnrichedDataDto } from '@redteclab/api/clients/bully'

import { type CmsGlossaryDefinition } from '../model/glossary/cmsGlossary'
import { type ContentType } from '../storyblok'

type StoryblokStoryContextValue = {
  enrichedData: CachedEnrichedStoryResponseGetCachedEnrichedStoryEnrichedDataDto | null
  glossaryDefinition: CmsGlossaryDefinition | null
  story: ISbStoryData<ContentType>
}

const StoryblokStoryContext = createContext<
  StoryblokStoryContextValue | undefined
>(undefined)

type StoryblokStoryProviderProps = PropsWithChildren<StoryblokStoryContextValue>

export const StoryblokStoryProvider: FC<StoryblokStoryProviderProps> = ({
  children,
  enrichedData,
  glossaryDefinition,
  story,
}) => {
  const value = useMemo(
    () => ({
      enrichedData,
      glossaryDefinition,
      story,
    }),
    [enrichedData, glossaryDefinition, story],
  )

  return (
    <StoryblokStoryContext.Provider value={value}>
      {children}
    </StoryblokStoryContext.Provider>
  )
}

export const useStoryblokStory = (): StoryblokStoryContextValue => {
  const context = useContext(StoryblokStoryContext)

  if (!context) {
    throw new Error('StoryblokStoryContext must be used within a CmsPage')
  }

  return context
}
